import { FormikProvider } from 'formik'

import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import Heading from 'share-ui/components/Heading/Heading'

import useLogin from 'pages/Auth/Login/useLogin'
import useGithubLogin from 'pages/Auth/Login/useGithubLogin'
import TextFieldFormik from 'components/TextFieldFormik'
import { StyledCenterFormContainer, StyledFormContainer } from 'styles/globalStyle.css'

import Button from 'share-ui/components/Button/Button'

import githubIcon from 'assets/icons/githubIcon.png'
import googleIcon from 'assets/tools/google.png'

import './login.css'

import OrDivider from 'components/OrDivider/OrDivider'
import TypographyTertiary from 'components/Typography/Tertiary'
import TypographyPrimary from 'components/Typography/Primary'
import HeadingSecondary from 'components/Heading/Secondary'
import { ButtonPrimary } from 'components/Button/Button'

import useGoogleLogin from './useGoogleLogin'
import { StyledRow } from 'pages/Subnets/panels/Playground/Subnets/Cortext/Settings/SettingsStyles.css'
import { StyledLogo } from 'pages/Navigation/MainNavigation'

const ErrorResendVerification = ({ resendVerifyEmail }: { resendVerifyEmail: () => void }) => (
    <p>
        <TypographyPrimary
            value='Please verify your email, didn’t receive verification email link?'
            size='small'
        />
        <StyledNavLink
            onClick={() => resendVerifyEmail()}
            className='text-secondary d-inline-block'
        >
            <u>Resend</u>
        </StyledNavLink>
    </p>
)

const Login = () => {
    const { t } = useTranslation()
    const {
        formik,
        showResendAlert,
        resendVerifyEmailHandle,
        handleNavigateRegister,
        authLoginCompleteLoading,
        handleForgotPassword,
    } = useLogin()
    const { githubLogin } = useGithubLogin()
    const { googleLogin } = useGoogleLogin()

    return (
        <StyledCenterFormContainer>
            {showResendAlert && (
                <ErrorResendVerification resendVerifyEmail={resendVerifyEmailHandle} />
            )}

            <StyledHeaderWrapper>
                <StyledRow>
                    <StyledLogo src={'/DaturaLogo.svg'} />

                    <HeadingSecondary
                        value={t('datura-ai')}
                        type={Heading.types?.h2}
                        style={{ fontSize: 24, lineHeight: 'normal' }}
                    />
                </StyledRow>
            </StyledHeaderWrapper>

            <StyledFormContainer>
                <ButtonPrimary
                    onClick={async () => {
                        const res = await githubLogin()
                        window.location.href = res.auth_url
                    }}
                >
                    <StyledInnerButtonWrapper>
                        <StyledImageWrapper secondary>
                            <StyledImg src={githubIcon} />
                        </StyledImageWrapper>
                        {t('log-in-with-github')}
                    </StyledInnerButtonWrapper>
                </ButtonPrimary>

                <ButtonPrimary
                    onClick={async () => {
                        const res = await googleLogin()
                        window.location.href = res.auth_url
                    }}
                >
                    <StyledInnerButtonWrapper>
                        <StyledImageWrapper secondary>
                            <StyledImg src={googleIcon} />
                        </StyledImageWrapper>
                        {t('log-in-with-google')}
                    </StyledInnerButtonWrapper>
                </ButtonPrimary>

                <OrDivider />

                <FormikProvider value={formik}>
                    <StyledInputWrapper>
                        <TextFieldFormik
                            label='Email'
                            field_name='email'
                            placeholder={t('enter-email')}
                            size='small'
                        />
                        <TextFieldFormik
                            label='Password'
                            field_name='password'
                            placeholder={t('enter-password')}
                            type='password'
                            size='small'
                        />
                    </StyledInputWrapper>
                </FormikProvider>

                <ButtonPrimary
                    onClick={formik.handleSubmit}
                    size={Button.sizes?.MEDIUM}
                    loading={authLoginCompleteLoading}
                    disabled={authLoginCompleteLoading}
                >
                    {t('start')}
                </ButtonPrimary>

                <StyledSignUpWrapper>
                    <button onClick={handleForgotPassword}>
                        <TypographyPrimary
                            value={`${t('forgot-password')}?`}
                            size={'small'}
                            style={{
                                textDecorationLine: 'underline',
                                cursor: 'pointer',
                                textAlign: 'center',
                                textUnderlineOffset: 5,
                                marginTop: 18,
                            }}
                        />
                    </button>
                </StyledSignUpWrapper>
                <StyledSignUpWrapper>
                    <TypographyTertiary value={t('dont-have-account')} size={'small'} />
                    <button onClick={handleNavigateRegister}>
                        <TypographyPrimary
                            value={t('sign-up')}
                            size={'small'}
                            style={{
                                textDecorationLine: 'underline',
                                cursor: 'pointer',
                                textAlign: 'center',
                                textUnderlineOffset: 5,
                                marginTop: 18,
                            }}
                        />
                    </button>
                </StyledSignUpWrapper>
            </StyledFormContainer>
        </StyledCenterFormContainer>
    )
}

export default Login

const StyledNavLink = styled.a`
    color: ${({ theme }) => theme.body.linkTextColor};
    cursor: pointer;

    font-size: 14px;
    margin-left: 5px;
`
const StyledSignUpWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
`

export const StyledInputWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`
export const StyledHeaderWrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 87px;
    align-items: center;
    text-align: center;
    gap: 22px;

    margin-top: 10px;
`

export const StyledImg = styled.img<{ customScale?: number }>`
    width: 16px;
    height: 16px;
    object-fit: cover;
    transform: scale(1.4);

    ${p =>
        p.customScale &&
        css`
            transform: scale(${p.customScale});
        `};
`
export const StyledImageWrapper = styled.div<{ secondary?: boolean }>`
    max-width: 20px;
    max-height: 20px;
    border-radius: 100px;
    border: 1px solid transparent;
    overflow: hidden;

    margin-bottom: 2px;

    ${p =>
        p.secondary &&
        css`
            border: 1px solid #fff;
        `};
`
export const StyledInnerButtonWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`
